import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

/**
 * Terms of Service Page
 * Site-wide terms covering all tools, services, data handling, and liability
 */
const TermsOfService = () => {
  const currentYear = new Date().getFullYear();

  const sectionStyle = {
    marginBottom: '2rem'
  };

  const headingStyle = {
    marginBottom: '1rem',
    color: '#212529'
  };

  return (
    <>
      <Helmet>
        <title>Terms of Service | Elliot Koss</title>
        <meta name="description" content="Terms of Service for elliotkoss.com - tools, newsletter, and all services" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Container style={{ paddingTop: '100px', paddingBottom: '2rem' }}>
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <h1 style={{ marginBottom: '2rem' }}>Terms of Service</h1>
            <p className="text-muted mb-4"><strong>Effective Date:</strong> January 17, 2026</p>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>1. About This Website</h2>
              <p>
                Welcome to elliotkoss.com! I'm Elliot Koss, and this is my personal portfolio
                website where I showcase my work and build new tools. Part of the purpose of
                this site is to experiment with new technologies and share useful tools with others.
              </p>
              <p>
                I do everything in my power to ensure that what I build is legal, compliant,
                and respectful of users' rights. If you have any concerns or issues with
                anything on this website, please reach out to me through the{' '}
                <Link to="/contact">contact form</Link> or connect with me on{' '}
                <a href="https://linkedin.com/in/elliotkoss" target="_blank" rel="noopener noreferrer">LinkedIn</a>.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>2. Acceptance of Terms</h2>
              <p>
                By accessing or using elliotkoss.com (the "Website") and any services,
                tools, or features offered through it (collectively, the "Services"),
                you agree to be bound by these Terms of Service ("Terms"). If you do
                not agree to these Terms, please do not use the Services.
              </p>
              <p>
                These Terms apply to all visitors, users, and others who access the Services,
                including users of my tools, newsletter subscribers, and general website visitors.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>3. Description of Services</h2>
              <p>The Services include, but are not limited to:</p>
              <ul>
                <li><strong>Article to Media Tool:</strong> Transforms article URLs into audio narration, social media posts with AI-generated images, and summary content</li>
                <li><strong>AI Notes Blog:</strong> Articles and insights on artificial intelligence and technology</li>
                <li><strong>Newsletter:</strong> Email updates about new content, tools, and announcements</li>
                <li><strong>Contact Forms:</strong> Methods to reach me with inquiries</li>
                <li><strong>Future Tools and Features:</strong> Additional tools and services I may introduce</li>
              </ul>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>4. User Accounts and Access Codes</h2>
              <p>
                Some Services may require or offer access codes for enhanced features or
                usage limits. If you receive an access code:
              </p>
              <ul>
                <li>You are responsible for maintaining its confidentiality</li>
                <li>You agree not to share, sell, or transfer your access code to others</li>
                <li>You are responsible for all activity under your access code</li>
                <li>I reserve the right to revoke access codes at any time</li>
              </ul>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>5. Acceptable Use</h2>
              <p>You agree to use the Services only for lawful purposes and in accordance with these Terms. You agree not to:</p>
              <ul>
                <li>Use the Services to process content you do not have the right to use</li>
                <li>Submit URLs to paywalled, private, or restricted content without authorization</li>
                <li>Attempt to circumvent any usage limits, access controls, or security measures</li>
                <li>Use automated systems, bots, or scripts to access the Services without permission</li>
                <li>Interfere with or disrupt the Services, servers, or networks</li>
                <li>Use the Services to generate content that is illegal, harmful, defamatory, or infringes on others' rights</li>
                <li>Attempt to reverse engineer, decompile, or extract source code from the Services</li>
                <li>Use the Services for any commercial purpose without my express written consent</li>
                <li>Impersonate any person or entity or misrepresent your affiliation</li>
              </ul>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>6. Content and Intellectual Property</h2>
              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>Your Content</h3>
              <p>
                You retain ownership of any content you submit to the Services. By using
                the Services, you grant me a limited, non-exclusive license to process
                your content solely for the purpose of providing the Services.
              </p>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>Generated Content</h3>
              <p>
                Content generated by my tools (audio, images, text summaries) is provided
                for your personal or business use. You are responsible for ensuring your
                use of generated content:
              </p>
              <ul>
                <li>Complies with applicable laws and regulations</li>
                <li>Respects the rights of original content creators</li>
                <li>Does not infringe any copyrights, trademarks, or other intellectual property rights</li>
              </ul>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>My Content</h3>
              <p>
                The Website, its original content (excluding user-submitted content),
                features, and functionality are owned by me and are protected by copyright,
                trademark, and other intellectual property laws.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>7. Newsletter and Communications</h2>
              <p>
                By subscribing to my newsletter, you agree to receive periodic emails
                about new content, tools, updates, and announcements. You may unsubscribe
                at any time using the link provided in each email.
              </p>
              <p>
                I may also send transactional emails related to your use of the Services
                (e.g., processing confirmations, account notifications).
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>8. Data Handling and Privacy</h2>
              <p>
                Your use of the Services is also governed by my{' '}
                <Link to="/privacy-policy">Privacy Policy</Link>, which describes how I
                collect, use, and protect your information. By using the Services, you
                consent to the data practices described in the Privacy Policy.
              </p>
              <p>
                <strong>Public Accessibility:</strong> Results generated by my tools are
                accessible via unique URLs and may be indexed by search engines.
                Anyone with the link can view the content. Do not process sensitive or
                private information if you do not want it to be publicly accessible.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>9. Usage Limits and Fair Use</h2>
              <p>
                The Services may impose usage limits to ensure fair access for all users
                and to manage operational costs. These limits may include:
              </p>
              <ul>
                <li>Daily processing caps that reset at midnight Central Time (America/Chicago)</li>
                <li>Rate limiting to prevent abuse</li>
                <li>Domain restrictions for supported content sources</li>
              </ul>
              <p>
                I reserve the right to modify usage limits at any time without prior notice.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>10. Third-Party Services</h2>
              <p>
                The Services integrate with and rely on third-party services, including
                but not limited to:
              </p>
              <ul>
                <li><strong>OpenAI:</strong> For AI-powered content generation (text, audio, images)</li>
                <li><strong>Amazon Web Services (AWS):</strong> For file storage and hosting</li>
                <li><strong>Analytics Services:</strong> Such as Google Analytics, FullStory, and Google Tag Manager for understanding usage patterns</li>
                <li><strong>Email Services:</strong> For newsletter delivery and transactional emails</li>
              </ul>
              <p>
                Your use of the Services is also subject to the terms and policies of
                these third-party providers. I am not responsible for the practices
                of third-party services.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>11. Disclaimer of Warranties</h2>
              <p>
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES
                OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED
                WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
                NON-INFRINGEMENT.
              </p>
              <p>
                I DO NOT WARRANT THAT:
              </p>
              <ul>
                <li>The Services will be uninterrupted, secure, or error-free</li>
                <li>Generated content will be accurate, complete, or suitable for any particular purpose</li>
                <li>Any errors or defects will be corrected</li>
                <li>The Services will meet your specific requirements</li>
              </ul>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>12. Limitation of Liability</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL I
                BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
                PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul>
                <li>Loss of profits, revenue, or data</li>
                <li>Business interruption</li>
                <li>Loss of goodwill or reputation</li>
                <li>Cost of substitute services</li>
              </ul>
              <p>
                WHETHER INCURRED DIRECTLY OR INDIRECTLY, EVEN IF I HAVE BEEN ADVISED
                OF THE POSSIBILITY OF SUCH DAMAGES. MY TOTAL LIABILITY SHALL NOT EXCEED
                THE AMOUNT YOU PAID ME, IF ANY, IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>13. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold me harmless from and against any claims,
                liabilities, damages, losses, costs, or expenses (including reasonable
                attorneys' fees) arising out of or related to:
              </p>
              <ul>
                <li>Your use of the Services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any rights of another party</li>
                <li>Content you submit to the Services</li>
              </ul>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>14. Termination</h2>
              <p>
                I reserve the right to terminate or suspend your access to the Services
                immediately, without prior notice or liability, for any reason, including
                without limitation if you breach these Terms.
              </p>
              <p>
                Upon termination, your right to use the Services will immediately cease.
                Provisions of these Terms that by their nature should survive termination
                shall survive, including ownership provisions, warranty disclaimers,
                indemnity, and limitations of liability.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>15. Changes to Terms</h2>
              <p>
                I reserve the right to modify these Terms at any time. Changes will be
                posted on this page with an updated "Last Updated" date. Material changes
                may be communicated through the Services or via email to newsletter subscribers.
              </p>
              <p>
                Your continued use of the Services after changes constitutes acceptance
                of the modified Terms. If you do not agree to the changes, you must stop
                using the Services.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>16. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the
                laws of the State of Texas, United States, without regard to its conflict
                of law provisions.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>17. Severability</h2>
              <p>
                If any provision of these Terms is held to be invalid or unenforceable,
                such provision shall be struck and the remaining provisions shall be
                enforced to the fullest extent under law.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>18. Entire Agreement</h2>
              <p>
                These Terms, together with the Privacy Policy, constitute the entire
                agreement between you and me regarding the Services and supersede all
                prior agreements and understandings.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>19. Contact</h2>
              <p>
                If you have questions about these Terms, please reach out through the{' '}
                <Link to="/contact">contact form</Link> or connect with me on{' '}
                <a href="https://linkedin.com/in/elliotkoss" target="_blank" rel="noopener noreferrer">LinkedIn</a>.
              </p>
            </section>

            <div className="text-muted mt-4" style={{ fontSize: '0.875rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
              <p style={{ marginBottom: '0.5rem' }}>
                © 2025{currentYear > 2025 ? `–${currentYear}` : ''} Elliot Koss. All rights reserved.
              </p>
              <p style={{ fontStyle: 'italic', marginBottom: 0 }}>
                This document was drafted with the assistance of Claude Code, an AI tool.
                Claude Code is not a lawyer, and this does not constitute legal advice.
                If you notice anything that should be corrected, please{' '}
                <Link to="/contact" style={{ color: '#6c757d' }}>let me know</Link>.
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default TermsOfService;
